import { Stack, Typography, Tooltip, Box } from "@mui/material";
import Badge from "../../Badge";
import { DateTime } from "luxon";
import { STATUS_COLORS } from "../../Badge/Badge.styled";
import { TextView } from "../..";
import IyugaTooltip from "../../IyugaTooltip";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoIcon from "@mui/icons-material/Info";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";

const nameCharacterLimit = 13;

// Utility function to format tooltip with username if name differs from username
const formatTooltipWithUsername = (referee, baseTooltip) => {
  if (!referee) return baseTooltip;

  const firstName = referee?.first_name || "";
  const lastName = referee?.last_name || "";
  const username = referee?.username || "";
  const email = referee?.email || "No email available";

  const fullName = `${firstName} ${lastName}`.trim();

  // If we have a username and it's different from the full name, show both with modern UI
  if (username && username !== fullName) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          minWidth: 200,
          p: 0.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#1976d2",
            fontSize: "13px",
          }}
        >
          <PersonIcon sx={{ fontSize: 16, color: "#1976d2" }} />
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, fontSize: "12px" }}
          >
            Username
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "#333",
            fontSize: "13px",
            fontWeight: 400,
            wordBreak: "break-word",
            mb: 0.5,
          }}
        >
          {username}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#1976d2",
            fontSize: "13px",
          }}
        >
          <EmailIcon sx={{ fontSize: 16, color: "#1976d2" }} />
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, fontSize: "12px" }}
          >
            Email
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "#333",
            fontSize: "13px",
            fontWeight: 400,
            wordBreak: "break-word",
          }}
        >
          {email}
        </Typography>
      </Box>
    );
  }

  // If only email is available or username is same as name, show just email
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 180,
        p: 0.5,
      }}
    >
      <EmailIcon sx={{ fontSize: 16, color: "#1976d2" }} />
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, fontSize: "11px", color: "#1976d2", mb: 0.2 }}
        >
          Email
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#333",
            fontSize: "12px",
            fontWeight: 400,
            wordBreak: "break-word",
          }}
        >
          {email}
        </Typography>
      </Box>
    </Box>
  );
};

// Reusable Commission Display Component
const CommissionDisplay = ({
  commissionValue,
  shouldShowTooltip,
  tooltipText,
}) => {
  const commissionDisplay = (
    <Typography
      sx={{
        fontSize: "12px",
        fontWeight: "500",
        color: "#565556",
      }}
    >
      {commissionValue != null ? `$${commissionValue}` : "N/A"}
    </Typography>
  );

  return shouldShowTooltip ? (
    <Box
      sx={{
        width: "100px",
        height: "40.71193313598633px",
        borderRadius: "4px",
        paddingTop: "4.43px",
        paddingRight: "8.86px",
        paddingBottom: "4.43px",
        paddingLeft: "8.86px",
        backgroundColor: "#EBEBEB",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {commissionDisplay}
      <IyugaTooltip tooltip={tooltipText} arrow placement="top">
        <InfoIcon
          sx={{
            fontSize: "16px",
            color: "#8F9094",
            cursor: "pointer",
            borderRadius: "50%",
          }}
        />
      </IyugaTooltip>
    </Box>
  ) : (
    commissionDisplay
  );
};

export const DIRECT_REFERRALS_TABLE_COLUMNS = [
  {
    name: "Date Subscribed",
    selector: (row) => tableDateFormmater(row?.date_created),
    sortable: true,
    sortField: "date_created",
  },
  {
    name: "Name",
    selector: (row) => {
      const firstName = row?.referee?.first_name || "";
      let lastName = row?.referee?.last_name || "";

      // Truncate last name if it's longer than nameCharacterLimit
      if (lastName.length > nameCharacterLimit) {
        lastName = `${lastName.slice(0, nameCharacterLimit)}...`;
      }

      const fullName = `${firstName} ${lastName}`.trim();
      const shouldStack = fullName.length > nameCharacterLimit; // Stack names longer than 13 characters

      return (
        <IyugaTooltip
          tooltip={formatTooltipWithUsername(
            row?.referee,
            row?.referee?.email || "No email available"
          )}
          arrow
          placement="right"
        >
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <img
              src={row?.referee?.profile_image}
              alt="Icon"
              width="28px"
              height="28px"
              style={{ flexShrink: 0, marginTop: shouldStack ? "2px" : "0" }}
            />

            {shouldStack ? (
              <Stack direction={"column"} spacing={0} alignItems={"flex-start"}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#565556",
                    textTransform: "capitalize",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {row?.referee?.first_name || ""}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#565556",
                    textTransform: "capitalize",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {lastName}
                </Typography>
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "#565556",
                  textTransform: "capitalize",
                  wordBreak: "break-word",
                }}
              >
                {fullName}
              </Typography>
            )}
          </Stack>
        </IyugaTooltip>
      );
    },
    sortable: true,
    sortField: "name",
  },
  {
    name: "Subscription Tier",
    selector: (row) => {
      const subscriptionValue = row?.subscription_type != null 
        ? row.subscription_type 
        : (!row.is_active ? 6 : row?.subscription_type) ?? "0";
      
      return (
        <Badge
          colorScheme={subscriptionValue}
        >
          {STATUS_COLORS[subscriptionValue] || "free"}
        </Badge>
      );
    },
    sortable: true,
    sortField: "subscription_type",
  },
  {
    name: "Subscription Fee (Monthly)",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.subscription_fee != null
          ? `$${(row.subscription_fee / 100)?.toFixed(2)}`
          : "N/A"}
      </Typography>
    ),
    sortable: true,
    sortField: "subscription_fee",
  },
  {
    name: "Commission %",
    selector: (row) => {
      let value = row?.commission_percent;
      let display = "0%";
      if (typeof value === "string") {
        if (value.trim().endsWith("%")) {
          display = value;
        } else if (!isNaN(Number(value))) {
          display = `${Number(value).toFixed(2)}%`;
        }
      } else if (value != null) {
        display = `${value.toFixed(2)}%`;
      }
      return (
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: "500",
            color: "#565556",
          }}
        >
          {display}
        </Typography>
      );
    },
  },
  {
    name: "Commission",
    selector: (row) => {
      const commissionValue =
        row?.monthly_income != null
          ? (row.monthly_income / 100)?.toFixed(2)
          : null;
      const shouldShowTooltip = row?.commission_estimated === true;

      return (
        <CommissionDisplay
          commissionValue={commissionValue}
          shouldShowTooltip={shouldShowTooltip}
          tooltipText="This is a predicted commission based on the user's subscription. The actual commission will be confirmed once the payment for the current month is received."
        />
      );
    },
    sortable: true,
    sortField: "monthly_income",
  },
];

export const AMBASSADORS_TABLE_COLUMNS = [
  {
    name: "Date Subscribed",
    selector: (row) => tableDateFormmater(row?.date_created),
    sortable: true,
    sortField: "date_created",
  },
  {
    name: "Name",
    selector: (row) => {
      const firstName = row?.referee?.first_name || "";
      let lastName = row?.referee?.last_name || "";

      // Truncate last name if it's longer than nameCharacterLimit
      if (lastName.length > nameCharacterLimit) {
        lastName = `${lastName.slice(0, nameCharacterLimit)}...`;
      }

      const fullName = `${firstName} ${lastName}`.trim();
      const shouldStack = fullName.length > nameCharacterLimit; // Stack names longer than 13 characters

      return (
        <Stack
          direction={"row"}
          gap={1}
          alignItems={"center"}
          justifyContent={"space-between"}
          width={"100%"}
        >
          {(!row?.is_account_connected_referee ||
            !row?.is_direct_referral_count_referee) && (
            <Tooltip
              title={
                <Box
                  sx={{
                    padding: "12px 16px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    boxShadow: "0px 4px 4px 0px #00000033",
                    maxWidth: "280px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <TextView
                    style={{
                      color: "#123051",
                    }}
                  >
                    You are unable to earn commissions from this Ambassador
                    until they fulfill their FA requirements:
                  </TextView>

                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {row?.is_account_connected_referee ? (
                        <CheckRoundedIcon
                          sx={{
                            color: "#4CAF50",
                            fontSize: "16px",
                          }}
                        />
                      ) : (
                        <CloseRoundedIcon
                          sx={{
                            color: "#F44336",
                            fontSize: "16px",
                          }}
                        />
                      )}
                      <TextView
                        style={{
                          color: "#123051",
                          fontWeight: "700",
                        }}
                      >
                        Sign up for Stripe Connect
                      </TextView>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={1}>
                      {row?.is_direct_referral_count_referee ? (
                        <CheckRoundedIcon
                          sx={{
                            color: "#4CAF50",
                            fontSize: "16px",
                          }}
                        />
                      ) : (
                        <CloseRoundedIcon
                          sx={{
                            color: "#F44336",
                            fontSize: "16px",
                          }}
                        />
                      )}
                      <TextView
                        style={{
                          fontWeight: "700",
                          color: "#123051",
                        }}
                      >
                        Enroll 5 iYuga Subscribers
                      </TextView>
                    </Stack>
                  </Stack>
                </Box>
              }
              arrow
              placement="top"
              TransitionProps={{
                timeout: {
                  enter: 300,
                  exit: 300,
                },
              }}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "transparent",
                    maxWidth: "none",
                    padding: 0,
                    animation:
                      "tooltipEnter 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    "@keyframes tooltipEnter": {
                      "0%": {
                        opacity: 0,
                        transform: "scale(0.8) translateY(10px)",
                      },
                      "100%": {
                        opacity: 1,
                        transform: "scale(1) translateY(0)",
                      },
                    },
                    "& .MuiTooltip-arrow": {
                      color: "white",
                      "&::before": {
                        backgroundColor: "white",
                        border: "none",
                      },
                    },
                  },
                },
              }}
            >
              <WarningRoundedIcon
                sx={{
                  color: "#F79A00",
                  fontSize: "18px",
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          )}
          <IyugaTooltip
            tooltip={formatTooltipWithUsername(
              row?.referee,
              row?.referee?.email || "No email available"
            )}
            placement="right"
          >
            <Box
              sx={{
                width: "100px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              {/* <img
                src={row?.referee?.profile_image}
                alt="Icon"
                width="28px"
                height="28px"
              /> */}

              {shouldStack ? (
                <Stack
                  direction={"column"}
                  spacing={0}
                  alignItems={"flex-start"}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#565556",
                      textTransform: "capitalize",
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                    }}
                  >
                    {row?.referee?.first_name || ""}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#565556",
                      textTransform: "capitalize",
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                    }}
                  >
                    {lastName}
                  </Typography>
                </Stack>
              ) : (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#565556",
                    textTransform: "capitalize",
                    wordBreak: "break-word",
                  }}
                >
                  {fullName}
                </Typography>
              )}
            </Box>
          </IyugaTooltip>
        </Stack>
      );
    },
    sortable: true,
    sortField: "name",
    width: "150px", // Make the name column wider
  },
  {
    name: "Enroller",
    selector: (row) => {
      const firstName = row?.referrer?.first_name || "";
      let lastName = row?.referrer?.last_name || "";

      // Truncate last name if it's longer than nameCharacterLimit
      if (lastName.length > nameCharacterLimit) {
        lastName = `${lastName.slice(0, nameCharacterLimit)}...`;
      }

      const fullName = `${firstName} ${lastName}`.trim();
      const shouldStack = fullName.length > nameCharacterLimit; // Stack names longer than 13 characters

      return (
        <IyugaTooltip
          tooltip={formatTooltipWithUsername(
            row?.referrer,
            row?.referrer?.email || "No email available"
          )}
          placement="right"
        >
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            {/* <img
              src={row?.referrer?.profile_image}
              alt="Icon"
              width="28px"
              height="28px"
            /> */}

            {shouldStack ? (
              <Stack direction={"column"} spacing={0} alignItems={"flex-start"}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#565556",
                    textTransform: "capitalize",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {row?.referrer?.first_name || ""}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#565556",
                    textTransform: "capitalize",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {lastName}
                </Typography>
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "#565556",
                  textTransform: "capitalize",
                  wordBreak: "break-word",
                }}
              >
                {fullName}
              </Typography>
            )}
          </Stack>
        </IyugaTooltip>
      );
    },
    sortable: true,
    sortField: "enroller",
  },
  {
    name: "Subscription Tier",
    selector: (row) => {
      const subscriptionValue = row?.subscription_type != null 
        ? row.subscription_type 
        : (!row.is_active ? 6 : row?.subscription_type) ?? "0";
      
      return (
        <Badge
          colorScheme={subscriptionValue}
        >
          {STATUS_COLORS[subscriptionValue] || "free"}
        </Badge>
      );
    },
    sortable: true,
    sortField: "subscription_type",
  },
  {
    name: "Subscription Fee (Monthly)",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.subscription_fee != null
          ? `$${(row.subscription_fee / 100)?.toFixed(2)}`
          : "N/A"}
      </Typography>
    ),
    sortable: true,
    sortField: "subscription_fee",
  },
  {
    name: "Commission %",
    selector: (row) => {
      let value = row?.commission_percent;
      let display = "0%";
      if (typeof value === "string") {
        if (value.trim().endsWith("%")) {
          display = value;
        } else if (!isNaN(Number(value))) {
          display = `${Number(value).toFixed(2)}%`;
        }
      } else if (value != null) {
        display = `${value.toFixed(2)}%`;
      }
      return (
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: "500",
            color: "#565556",
          }}
        >
          {display}
        </Typography>
      );
    },
  },
  {
    name: "Commission",
    selector: (row) => {
      const commissionValue =
        row?.monthly_income != null
          ? (row.monthly_income / 100)?.toFixed(2)
          : null;
      const shouldShowTooltip = row?.commission_estimated === true;

      return (
        <CommissionDisplay
          commissionValue={commissionValue}
          shouldShowTooltip={shouldShowTooltip}
          tooltipText="This is a predicted commission based on the user's subscription. The actual commission will be confirmed once the payment for the current month is received."
        />
      );
    },
    sortable: true,
    sortField: "monthly_income",
  },

  {
    name: "Tri-Level Revenue",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.tri_level_commission != null
          ? `$${(row.tri_level_commission / 100)?.toFixed(2)}`
          : "N/A"}
      </Typography>
    ),
    sortable: true,
    sortField: "tri_level_commission",
  },
  {
    name: "Ambassadors",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.ambassadors ?? "N/A"}
      </Typography>
    ),
    // sortable: true,
  },
  {
    name: "Referrals",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.active_referrals ?? "N/A"}
      </Typography>
    ),
    // sortable: true,
  },
  {
    name: "Inactive",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.inactive_referrals ?? "N/A"}
      </Typography>
    ),
    // sortable: true,
  },
  {
    name: "Free",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.free_referrals ?? "N/A"}
      </Typography>
    ),
    // sortable: true,
  },
  {
    name: "Essential",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.essential_referrals ?? "N/A"}
      </Typography>
    ),
    // sortable: true,
  },
  {
    name: "Premium",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.premium_referrals ?? "N/A"}
      </Typography>
    ),
    // sortable: true,
  },
  {
    name: "ProSuite",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.infinity_referrals ?? "N/A"}
      </Typography>
    ),
    // sortable: true,
  },
];

export const TRI_LEVEL_TABLE_COLUMNS = [
  {
    name: "Date Subscribed",
    selector: (row) => tableDateFormmater(row?.date_created),
    sortable: true,
    sortField: "date_created",
  },
  {
    name: "Name",
    selector: (row) => {
      const firstName = row?.referee?.first_name || "";
      let lastName = row?.referee?.last_name || "";

      // Truncate last name if it's longer than nameCharacterLimit
      if (lastName.length > nameCharacterLimit) {
        lastName = `${lastName.slice(0, nameCharacterLimit)}...`;
      }

      const fullName = `${firstName} ${lastName}`.trim();
      const shouldStack = fullName.length > nameCharacterLimit; // Stack names longer than 13 characters

      return (
        <IyugaTooltip
          tooltip={formatTooltipWithUsername(
            row?.referee,
            row?.referee?.email || "No email available"
          )}
          placement="right"
        >
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <img
              src={row?.referee?.profile_image}
              alt="Icon"
              width="28px"
              height="28px"
              style={{ flexShrink: 0, marginTop: shouldStack ? "2px" : "0" }}
            />

            {shouldStack ? (
              <Stack direction={"column"} spacing={0} alignItems={"flex-start"}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#565556",
                    textTransform: "capitalize",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {row?.referee?.first_name || ""}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#565556",
                    textTransform: "capitalize",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {lastName}
                </Typography>
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "#565556",
                  textTransform: "capitalize",
                  wordBreak: "break-word",
                }}
              >
                {fullName}
              </Typography>
            )}
          </Stack>
        </IyugaTooltip>
      );
    },
    sortable: true,
    sortField: "name",
  },
  {
    name: "Enroller",
    selector: (row) => {
      const firstName = row?.referrer?.first_name || "";
      let lastName = row?.referrer?.last_name || "";

      // Truncate last name if it's longer than nameCharacterLimit
      if (lastName.length > nameCharacterLimit) {
        lastName = `${lastName.slice(0, nameCharacterLimit)}...`;
      }

      const fullName = `${firstName} ${lastName}`.trim();
      const shouldStack = fullName.length > nameCharacterLimit; // Stack names longer than 13 characters

      return (
        <IyugaTooltip
          tooltip={formatTooltipWithUsername(
            row?.referrer,
            row?.referrer?.email || "No email available"
          )}
          placement="right"
        >
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <img
              src={row?.referrer?.profile_image}
              alt="Icon"
              width="28px"
              height="28px"
              style={{ flexShrink: 0, marginTop: shouldStack ? "2px" : "0" }}
            />

            {shouldStack ? (
              <Stack direction={"column"} spacing={0} alignItems={"flex-start"}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#565556",
                    textTransform: "capitalize",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {row?.referrer?.first_name || ""}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#565556",
                    textTransform: "capitalize",
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {lastName}
                </Typography>
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "#565556",
                  textTransform: "capitalize",
                  wordBreak: "break-word",
                }}
              >
                {fullName}
              </Typography>
            )}
          </Stack>
        </IyugaTooltip>
      );
    },
    sortable: true,
    sortField: "enroller",
  },
  {
    name: "Subscription Tier",
    selector: (row) => {
      const subscriptionValue = row?.subscription_type != null 
        ? row.subscription_type 
        : (!row.is_active ? 6 : row?.subscription_type) ?? "0";
      
      return (
        <Badge
          colorScheme={subscriptionValue}
        >
          {STATUS_COLORS[subscriptionValue] || "Free"}
        </Badge>
      );
    },
    sortable: true,
    sortField: "subscription_type",
  },
  {
    name: "Subscription Fee (Monthly)",
    selector: (row) => (
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: "500",
          color: "#565556",
        }}
      >
        {row?.subscription_fee != null
          ? `$${(row.subscription_fee / 100)?.toFixed(2)}`
          : "N/A"}
      </Typography>
    ),
    sortable: true,
    sortField: "subscription_fee",
  },
  {
    name: "Commission %",
    selector: (row) => {
      let value = row?.commission_percent;
      let display = "0%";
      if (typeof value === "string") {
        if (value.trim().endsWith("%")) {
          display = value;
        } else if (!isNaN(Number(value))) {
          display = `${Number(value).toFixed(2)}%`;
        }
      } else if (value != null) {
        display = `${value.toFixed(2)}%`;
      }
      return (
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: "500",
            color: "#565556",
          }}
        >
          {display}
        </Typography>
      );
    },
  },
  {
    name: "Commission",
    selector: (row) => {
      const commissionValue =
        row?.monthly_income != null
          ? (row.monthly_income / 100)?.toFixed(2)
          : null;
      const shouldShowTooltip = row?.commission_estimated === true;

      return (
        <CommissionDisplay
          commissionValue={commissionValue}
          shouldShowTooltip={shouldShowTooltip}
          tooltipText="This is a predicted commission based on the user's subscription. The actual commission will be confirmed once the payment for the current month is received."
        />
      );
    },
    sortable: true,
    sortField: "monthly_income",
  },
];

export const tableDateFormmater = (date) => {
  if (!date) return "N/A";

  const dateObj = DateTime.fromISO(date);

  // Format as MM/DD/YY with leading zeros
  const month = dateObj.month.toString().padStart(2, "0");
  const day = dateObj.day.toString().padStart(2, "0");
  const year = dateObj.year.toString().slice(-2); // Get last 2 digits of year

  return `${month}/${day}/${year}`;
};
